<script setup>
import fanTop1Png from '@/components/Ranking/Assets/fans-badges/fans-top-1.png'
import fanTop2Png from '@/components/Ranking/Assets/fans-badges/fans-top-2.png'
import fanTop3Png from '@/components/Ranking/Assets/fans-badges/fans-top-3.png'
import fanTop4Png from '@/components/Ranking/Assets/fans-badges/fans-top-4.png'
import fanTop5Png from '@/components/Ranking/Assets/fans-badges/fans-top-5.png'
import fanTop6Png from '@/components/Ranking/Assets/fans-badges/fans-top-6.png'
import { inject, computed } from 'vue'

const props = defineProps({
  rank: {
    type: Number,
    default: 0,
    required: true
  },
  timeframe: {
    type: String,
    default: '30d',
    required: true
  }
})

const isClickable = inject('isClickable', false)

const component = computed(() => {
  return isClickable ? 'router-link' : 'span'
})

const timeframeToHuman = new Map([
  ['1d', 'Daily'],
  ['7d', 'Weekly'],
  ['30d', 'Monthly']
])

const humanTimeframe = computed(() => {
  return timeframeToHuman.get(props.timeframe)
})

const badgeSetting = computed(() => {
  const rank = props.rank

  if (rank === 1) {
    return {
      image: fanTop1Png,
      outerClass: 'bg-gradient-to-r from-[#F234AC] to-[#FF5C7C]',
      innerClass: 'bg-gradient-to-r from-[#FF5C7C] to-[#F234AC]',
      top: 1
    }
  } else if (rank === 2) {
    return {
      image: fanTop2Png,
      outerClass: 'bg-gradient-to-r from-[#F29334] to-[#FFBB00]',
      innerClass: 'bg-gradient-to-r from-[#FFBB00] to-[#F29334]',
      top: 2
    }
  } else if (rank === 3) {
    return {
      image: fanTop3Png,
      outerClass: 'bg-gradient-to-r from-[#85A4A7] to-[#A9D6DB]',
      innerClass: 'bg-gradient-to-r from-[#A9D6DB] to-[#85A4A7]',
      top: 3
    }
  } else if (rank >= 4 && rank <= 10) {
    return {
      image: fanTop4Png,
      outerClass: 'bg-gradient-to-r from-[#E26A00] to-[#FFAE5C]',
      innerClass: 'bg-gradient-to-r from-[#FFAE5C] to-[#E26A00]',
      top: 10
    }
  } else if (rank >= 11 && rank <= 20) {
    return {
      image: fanTop5Png,
      outerClass: 'bg-gradient-to-r from-[#355289] to-[#588ABA]',
      innerClass: 'bg-gradient-to-r from-[#588ABA] to-[#355289]',
      top: 20
    }
  } else if (rank >= 21 && rank <= 50) {
    return {
      image: fanTop6Png,
      outerClass: 'bg-gradient-to-r from-[#F23459] to-[#FF6200]',
      innerClass: 'bg-gradient-to-r from-[#FF6200] to-[#F23459]',
      top: 50
    }
  } else if (rank >= 51 && rank <= 100) {
    return {
      image: null,
      outerClass: 'bg-gradient-to-r from-[#F15CFF] to-[#F234AC]',
      innerClass: 'bg-gradient-to-r from-[#F234AC] to-[#F15CFF]',
      shapeClass: 'bg-[#FFD0DF]',
      svgClass: 'text-[#FF00DD]',
      top: 100
    }
  } else if (rank >= 101 && rank <= 500) {
    return {
      image: null,
      outerClass: 'bg-gradient-to-r from-[#F15CFF] to-[#F15CFF]',
      innerClass: 'bg-gradient-to-r from-[#E662FF] to-[#7B91FF]',
      shapeClass: 'bg-[#D0E5FF]',
      svgClass: 'text-[#E662FF]',
      top: 500
    }
  } else if (rank >= 501 && rank <= 1000) {
    return {
      image: null,
      outerClass: 'bg-gradient-to-r from-[#7B91FF] to-[#F15CFF]',
      innerClass: 'bg-gradient-to-r from-[#7B91FF] to-[#00CAB2]',
      shapeClass: 'bg-[#ffffff]',
      svgClass: 'text-[#7B93FE]',
      top: 1000
    }
  } else {
    return null
  }
})
</script>

<template>
  <component
    :is="component"
    v-if="badgeSetting && humanTimeframe"
    :to="{ path: '/ranking', query: { tab: `consumers_${timeframe}` }}"
    :class="badgeSetting.outerClass"
    class="p-px inline-flex rounded-full whitespace-nowrap">
    <span
      :class="[badgeSetting.innerClass, [(rank >= 51 && rank <= 1000) ? 'pl-3.5' : 'pl-6']]"
      class="relative inline-flex items-center rounded-full px-1.5 text-[9px] leading-3 font-bold text-white">
      <img
        v-if="badgeSetting.image"
        :src="badgeSetting.image"
        alt="Badge"
        :class="[(rank >= 51 && rank <= 1000) ? 'w-6 left-0' : 'w-5 left-0.5']"
        class="absolute">

      <span
        v-if="(rank >= 51 && rank <= 1000)"
        :class="badgeSetting.shapeClass"
        class="absolute left-px w-2.5 h-2.5 rounded-full flex items-center justify-center">
        <InlineSvg
          :class="badgeSetting.svgClass"
          :src="require('@/assets/fontawesome/solid/star.svg')"
          class="w-2 h-2 p-px"/>
      </span>

      {{ humanTimeframe }} TOP {{ badgeSetting.top }}
    </span>
  </component>
</template>